import {gql} from '@apollo/client';
import React, {useMemo} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {PLATE_DESIGN_TEMPLATE_TABLE_NAME} from 'notebook/template-collections/TemplateCollectionTypes';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import {
  ListPlateDesignTemplatesQuery,
  ListPlateDesignTemplatesQueryVariables,
  ListPlateDesignTemplatesQuery_collectiblesListing_TemplateCollectiblesListingNode_collectibles_PlateDesignTemplateNode as PlateDesignTemplateCollectible,
} from './__generated/ListPlateDesignTemplatesQuery';

export const LIST_PLATE_DESIGN_TEMPLATES_QUERY = gql`
  query ListPlateDesignTemplatesQuery(
    $searchInput: StrictString
    $typeFilter: [StrictString!]
    $archiveFilter: [StrictString]
    $limitOffsetInput: LimitOffsetInput
  ) @skipAudit @engComponent(name: NOTEBOOK) {
    collectiblesListing(
      searchInput: $searchInput
      typeFilter: $typeFilter
      archiveFilter: $archiveFilter
      limitOffsetInput: $limitOffsetInput
    ) {
      total
      collectibles {
        ... on TemplateCollectibleInterface {
          id
          name
        }
        ... on PlateDesignTemplateNode {
          id
          name
          scratchPlate {
            ... on ScratchPlateNode {
              id
            }
          }
        }
      }
    }
  }
`;

interface PlateDesignTemplateSelectorProps {
  selectedTemplateId: string | null;
  onSelectTemplate: (templateId: string, scratchPlateId: string) => void;
}

export default function PlateDesignTemplateSelector({
  selectedTemplateId,
  onSelectTemplate,
}: PlateDesignTemplateSelectorProps): JSX.Element {
  const result = useSkippableBenchlingQuery<
    ListPlateDesignTemplatesQuery,
    ListPlateDesignTemplatesQueryVariables
  >(LIST_PLATE_DESIGN_TEMPLATES_QUERY, {
    variables: {
      searchInput: null,
      typeFilter: [PLATE_DESIGN_TEMPLATE_TABLE_NAME],
      archiveFilter: [null],
      limitOffsetInput: {limit: 50, offset: 0},
    },
  });

  const {templates, scratchPlateIdByTemplateId} = useMemo(() => {
    if (result.type !== QueryResultType.SUCCESS) {
      return {templates: [], scratchPlateIdByTemplateId: new Map<string, string>()};
    }
    const accessibleTemplates = result.data.collectiblesListing.collectibles.flatMap((c) => {
      if (c.__typename !== 'PlateDesignTemplateNode' || isInaccessible(c.scratchPlate)) {
        return [];
      }
      return [{template: c, scratchPlateId: c.scratchPlate.id}];
    });
    return {
      templates: accessibleTemplates.map(({template}) => template),
      scratchPlateIdByTemplateId: new Map(
        accessibleTemplates.map(({template, scratchPlateId}) => [template.id, scratchPlateId])
      ),
    };
  }, [result]);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) ?? null;
  const optionDatas = templates.map((t) => ({name: t.name, value: t}));

  return (
    <SelectDropdown
      displayName={selectedTemplate?.name ?? 'Select template...'}
      menu={
        <SearchableMenu
          onSelect={(value) => {
            const template = value as PlateDesignTemplateCollectible;
            const scratchPlateId = scratchPlateIdByTemplateId.get(template.id);
            if (scratchPlateId != null) {
              onSelectTemplate(template.id, scratchPlateId);
            }
          }}
          optionDatas={optionDatas}
          selectedValue={selectedTemplate ?? undefined}
          width={Menu.WIDTHS.FULL}
        />
      }
      dataTestAttrs={{component: 'PlateDesignTemplateSelector', element: 'root'}}
    />
  );
}
